import {CanActivateFn, Router, Routes} from '@angular/router';
import {inject} from '@angular/core';
import {UserService} from './core/auth/services/user.service';
import {Role} from './core/auth/user.model';
import {map, take} from 'rxjs/operators';
import {AppComponent} from './app.component';
import {AuthWelcomeComponent} from './features/auth/welcome/auth-welcome.component';
import {AuthLoginComponent} from './features/auth/login/auth-login.component';
import {AuthSignupComponent} from './features/auth/signup/auth-signup.component';
import {AuthFlowComponent} from './features/auth/flow/auth-flow.component';
import {ConfirmationScreenComponent} from './features/auth/confirmation/confirmation-screen.component';
import {AuthProfileComponent} from './features/auth/profile/auth-profile.component';
import {WelcomeScreenComponent} from './features/auth/welcome-owner/welcome-screen.component';
import {AddPropertyFlowComponent} from './features/owner/add-property-flow.component';
import {AccountVerifiedComponent} from './features/auth/account-verified/account-verified.component';

const requireAuth = () => {
  const router = inject(Router);
  return inject(UserService).isAuthenticated.pipe(map(isAuth => isAuth || router.createUrlTree(['/welcome'])));
};

export const publicGuard: CanActivateFn = () => {
  const userService = inject(UserService);
  const router = inject(Router);

  return userService.isAuthenticated.pipe(
    take(1),
    map(isAuth => {
      if (!isAuth) return true;
      const user = userService.getCurrentUserSync();
      if (user?.activeRole == null) {
        return user?.emailVerifiedAt == null
          ? router.createUrlTree(['/setup/confirmation'])
          : router.createUrlTree(['/setup/profile']);
      }
      return user.activeRole === Role.LANDLORD
        ? router.createUrlTree(['/legacy/dashboard'])
        : router.createUrlTree(['/legacy/tenant-welcome']);
    })
  );
};


export const requireProfileIncompleteGuard: CanActivateFn = () => {
  const user = inject(UserService).getCurrentUserSync();
  const router = inject(Router);

  if (user?.activeRole != null) {
    return user.activeRole === Role.LANDLORD
      ? router.createUrlTree(['/legacy/dashboard'])
      : router.createUrlTree(['/legacy/tenant-welcome']);
  }
  return true;
};

export const requireEmailUnverifiedGuard: CanActivateFn = () => {
  const user = inject(UserService).getCurrentUserSync();
  const router = inject(Router);

  if (user?.emailVerifiedAt != null) {
    if (user.activeRole == null) {
      return router.createUrlTree(['/setup/profile']);
    }
    return user.activeRole === Role.LANDLORD
      ? router.createUrlTree(['/legacy/dashboard'])
      : router.createUrlTree(['/legacy/tenant-welcome']);
  }
  return true;
};

export const routes: Routes = [
  {path: 'welcome', component: AuthWelcomeComponent, canActivate: [publicGuard]},
  {path: 'signin', component: AuthLoginComponent, canActivate: [publicGuard]},
  {path: 'signup', component: AuthSignupComponent, canActivate: [publicGuard]},
  {path: 'verify-email', component: AccountVerifiedComponent},
  {
    path: 'setup',
    component: AuthFlowComponent,
    canActivate: [requireAuth],
    children: [
      {path: 'confirmation', component: ConfirmationScreenComponent, canActivate: [requireEmailUnverifiedGuard]},
      {path: 'profile', component: AuthProfileComponent, canActivate: [requireProfileIncompleteGuard]},
      {path: 'welcome-owner', component: WelcomeScreenComponent},
      {path: 'add-property', component: AddPropertyFlowComponent},
    ]
  }, {
    path: 'tenant',
    canActivate: [requireAuth],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/tenant/app/tenant-app.component').then(m => m.TenantAppComponent),
      },
      {
        path: ':view',
        loadComponent: () =>
          import('./features/tenant/app/tenant-app.component').then(m => m.TenantAppComponent),
      },
    ]
  },
  {
    path: 'legacy',
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/legacy/dashboard.component').then(m => m.LegacyDashboardComponent),
      },
      {
        path: 'add-property',
        loadComponent: () =>
          import('./features/legacy/add-property.component').then(m => m.LegacyAddPropertyComponent),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/legacy/profile.component').then(m => m.LegacyProfileComponent),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/legacy/settings.component').then(m => m.LegacySettingsComponent),
      },
      {
        path: 'navigation',
        loadComponent: () =>
          import('./features/legacy/navigation.component').then(m => m.LegacyNavigationComponent),
      },
      {
        path: 'search',
        loadComponent: () =>
          import('./features/tenant/dashboard-search/search-properties.component').then(m => m.SearchPropertiesComponent),
      },
      {
        path: 'tenant-welcome',
        loadComponent: () =>
          import('./features/tenant/home/tenant-home.component').then(m => m.TenantHomeComponent),
      },
    ],
  },
  {
    path: 'properties/:id',
    loadComponent: () =>
      import('./features/tenant/components/property-detail/app-property-detail').then(m => m.PropertyDetail),
  },
  {
    path: '',
    loadComponent: () => import('./features/shell/shell.component').then(m => m.ShellComponent),
  }, {
    path: 'test',
    component: AppComponent,
    canActivate: [requireAuth],
  },
];
